import { Link } from 'react-router-dom'
import { Linkedin, Target, Lightbulb, Zap, Users, ArrowRight } from 'lucide-react'
import FloatingBackground from '../components/FloatingBackground'
import Seo from '../components/Seo'
import { FadeIn } from '../components/Motion'

const companyFacts = [
  { label: 'Founded', value: '2024' },
  { label: 'Headquarters', value: 'Madison, Wisconsin' },
  { label: 'Structure', value: 'Wisconsin C Corporation' },
  { label: 'Focus', value: 'Feedback to action — conversational surveys' },
]

const team = [
  {
    name: 'Dr. Christopher Harrison',
    role: 'Founder & CEO',
    bio: '14+ years building research infrastructure at the University of Wisconsin School of Medicine, supporting 1,500+ researchers. PhD in Computer Science studying fault detection in complex systems. Founding chair of MadAI (560+ members), co-chair of Big Data Madison (2,700+ members), founding chair of IEEE Computer Society Madison Chapter.',
    linkedin: '#',
  },
]

const approach = [
  { icon: Target, title: 'Asks follow-up questions', description: 'Based on each response', color: '#3b82f6' },
  { icon: Lightbulb, title: 'Probes deeper', description: 'When it detects something important', color: '#8b5cf6' },
  { icon: Zap, title: 'Synthesizes automatically', description: 'Themes, sentiment, and stories', color: '#00008B' },
  { icon: Users, title: 'Delivers in hours', description: 'What used to take weeks', color: '#3b82f6' },
]

export default function About() {
  return (
    <div>
      <Seo
        title="About PrismScope"
        description="PrismScope is a feedback-to-action platform founded in Madison, Wisconsin. We close the gap between fast-but-shallow surveys and deep-but-slow interviews."
        path="/about"
      />

      {/* Hero */}
      <section className="relative bg-white py-20 md:py-28 overflow-hidden">
        <FloatingBackground />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              About PrismScope
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.95]">
              Closing the{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B] bg-clip-text text-transparent">
                  feedback gap
                </span>
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 bg-[#8b5cf6]/25 -z-0 -skew-y-1"
                />
              </span>
              <span className="text-[#00008B]">.</span>
            </h1>
            <p className="text-xl md:text-2xl text-intelligence-700 max-w-3xl mx-auto leading-relaxed">
              We believe everyone deserves to be heard — and organizations deserve to understand.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Mission Statement — chunky card */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-2xl bg-[#00008B] translate-x-2 translate-y-2"
              />
              <div className="relative bg-white border-2 border-intelligence-900 rounded-2xl p-8 md:p-12">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
                  Our mission
                </p>
                <p className="text-xl md:text-2xl text-intelligence-700 leading-relaxed">
                  PrismScope was founded to close the feedback gap — delivering the depth of qualitative
                  research at the speed and scale modern organizations demand.
                </p>
                <p className="text-xl md:text-2xl text-intelligence-700 leading-relaxed mt-6">
                  Traditional surveys give you data. Human interviews give you understanding.{' '}
                  <span className="font-bold underline decoration-[#8b5cf6] decoration-4 underline-offset-4">
                    We built PrismScope to give you both.
                  </span>
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* The Problem — chunky cards */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                The problem
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                The Feedback Gap Is Real
              </h2>
              <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
                Organizations face an impossible choice.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                kicker: 'Option A',
                title: 'Fast but shallow',
                body: 'Surveys return checkbox data that tells you WHAT but never WHY. Response rates are declining. Insights are surface-level.',
                color: '#3b82f6',
              },
              {
                kicker: 'Option B',
                title: 'Deep but slow',
                body: "Human interviews uncover real understanding, but they're expensive ($150-300 each), hard to schedule, and impossible to scale.",
                color: '#8b5cf6',
              },
            ].map((card, i) => (
              <FadeIn key={card.title} delay={i * 0.1}>
                <div className="relative h-full">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-xl translate-x-2 translate-y-2"
                    style={{ backgroundColor: card.color }}
                  />
                  <div className="relative bg-white border-2 border-intelligence-900 rounded-xl p-8 h-full">
                    <span
                      className="inline-block font-mono text-xs uppercase tracking-widest mb-3 px-2 py-0.5 border border-intelligence-900 rounded"
                      style={{ color: card.color }}
                    >
                      {card.kicker}
                    </span>
                    <h3 className="text-2xl font-bold text-intelligence-900 mb-3">{card.title}</h3>
                    <p className="text-intelligence-700 leading-relaxed">{card.body}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.25}>
            <p className="text-center text-lg text-intelligence-700 mt-12 max-w-2xl mx-auto">
              The gap between these options is where real understanding gets lost — and where bad
              decisions get made.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Our Approach — chunky icon tiles */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                Our approach
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                Conversations at Scale
              </h2>
              <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
                PrismScope uses AI to conduct real conversations — not static questionnaires.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approach.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.06}>
                <div className="relative h-full">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-xl translate-x-1.5 translate-y-1.5"
                    style={{ backgroundColor: item.color }}
                  />
                  <div className="relative bg-white rounded-xl border-2 border-intelligence-900 p-6 text-center h-full">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4 border-2 border-intelligence-900"
                      style={{ backgroundColor: `${item.color}15` }}
                    >
                      <item.icon className="w-6 h-6" style={{ color: item.color }} />
                    </div>
                    <h3 className="font-bold text-intelligence-900 mb-1">{item.title}</h3>
                    <p className="text-intelligence-600 text-sm">{item.description}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.35}>
            <p className="text-center text-lg text-intelligence-700 mt-12">
              The result:{' '}
              <span className="font-bold underline decoration-[#8b5cf6] decoration-4 underline-offset-4">
                qualitative depth at survey speed and price.
              </span>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                Who built it
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
                Our Team
              </h2>
            </div>
          </FadeIn>
          <div className="max-w-2xl mx-auto">
            {team.map((member, i) => (
              <FadeIn key={member.name} delay={i * 0.1}>
                <div className="text-center">
                  <div className="relative inline-block mb-6">
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 rounded-full bg-[#3b82f6] translate-x-2 translate-y-2"
                    />
                    <div className="relative w-32 h-32 rounded-full overflow-hidden bg-intelligence-100 border-2 border-intelligence-900 flex items-center justify-center">
                      <span className="text-4xl font-bold text-intelligence-700">
                        {member.name.split(' ').map((n) => n[0]).join('')}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-intelligence-900">{member.name}</h3>
                  <p className="text-[#8b5cf6] font-semibold mb-4">{member.role}</p>
                  <p className="text-intelligence-600 leading-relaxed">{member.bio}</p>
                  <div className="flex justify-center mt-4">
                    <a
                      href={member.linkedin}
                      className="text-intelligence-400 hover:text-[#3b82f6] transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Company Facts — retro outlined */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                Company facts
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">
                The basics
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-xl bg-[#8b5cf6] translate-x-2 translate-y-2"
              />
              <div className="relative bg-white rounded-xl border-2 border-intelligence-900 overflow-hidden">
                <div className="divide-y-2 divide-intelligence-200">
                  {companyFacts.map((fact) => (
                    <div key={fact.label} className="flex justify-between px-6 py-4">
                      <span className="font-mono text-xs uppercase tracking-widest text-intelligence-500 self-center">
                        {fact.label}
                      </span>
                      <span className="text-intelligence-900 font-bold">{fact.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-intelligence-900 py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">
              Want to talk?
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Want to learn more?</h2>
            <p className="text-lg text-intelligence-400 mb-10 max-w-2xl mx-auto">
              Get in touch with our team to discuss how PrismScope can help your organization.
            </p>
            <div className="relative inline-block group">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-lg bg-[#3b82f6] translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2"
              />
              <Link
                to="/contact"
                className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-intelligence-900 bg-white border-2 border-white rounded-lg"
              >
                Contact us <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
